import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import Notice from '../interfaces/notice';
import User from '../interfaces/user';
import Farm from '../interfaces/farm';
import SystemConfiguration from '../interfaces/systemConfiguration';
import SystemConfigurationValues from '../interfaces/systemConfigurationValues';
import { FarmUser } from '../interfaces/farm-user';
import { RedirectFeature } from '../pages/crop/fields-and-works/fields/components/newmap';

export const DEFAULT_REDUCING_MEDIA_QUERY = '(max-width: 1279px)';

interface maintenanceModeMessage {
  title: string;
  content: string;
}

export interface GlobalState {
  loading: number;
  loadingFragments: { [key: string]: number };
  user: User;
  guard: string;
  farm: Farm;
  farmUser: FarmUser;
  season: string;
  notices: Notice[];
  maintenanceMode: maintenanceModeMessage | false;
  isSidebarMenuOpen: boolean;
  isSidebarMenuReduced: boolean;
  isProfileMenuOpen: boolean;
  systemConfiguration: SystemConfigurationValues;
  movementType: string;
  fieldsFilter: string | null;
  isMLTotalsChanged: boolean | null;
  isAnimalCalendarOpen: boolean;
  split: RedirectFeature[];
  merge: RedirectFeature;
}

const globalSlice = createSlice({
  name: 'global',
  initialState: {
    loading: 0,
    loadingFragments: {},
    user: null,
    guard: null,
    farm: null,
    farmUser: null,
    season: new Date().getFullYear().toString(),
    notices: [],
    maintenanceMode: false,
    isSidebarMenuOpen: false,
    isSidebarMenuReduced: window.matchMedia(DEFAULT_REDUCING_MEDIA_QUERY)
      .matches,
    isProfileMenuOpen: false,
    movementType: null,
    systemConfiguration: {},
    fieldsFilter: null,
    isMLTotalsChanged: false,
    isAnimalCalendarOpen: true,
    split: [],
    merge: null,
  } as GlobalState,
  /**
   * Redux Toolkit allows us to write "mutating" logic in reducers. It doesn't actually mutate the state because it uses the Immer library,
   * which detects changes to a "draft state" and produces a brand new immutable state based off those changes
   */
  reducers: {
    loading: {
      reducer: (state, action: PayloadAction<Boolean>) => {
        state.loading += action.payload ? 1 : -1;
      },
      // Using prepare, to set default value of payload to true.
      prepare: (loading: boolean = true) => {
        return { payload: loading };
      },
    },
    loadingFragments: (
      state,
      action: PayloadAction<{ [key: string]: boolean }>
    ) => {
      Object.keys(action.payload).forEach((key) => {
        state.loadingFragments[key] =
          (state.loadingFragments[key] || 0) + (action.payload[key] ? 1 : -1);
        if (state.loadingFragments[key] <= 0) {
          delete state.loadingFragments[key];
        }
      });
    },
    user: (state, action) => {
      state.user = action.payload;

      const farms = action.payload?.farms;

      if (Array.isArray(farms)) {
        const pathSegments = window.location.pathname.split('/');
        const farmIdIndex =
          pathSegments.findIndex((segment) => segment === 'farms') + 1;
        const farmIdFromUrl = pathSegments[farmIdIndex];
        const farmId = sessionStorage.getItem('farmId') || farmIdFromUrl;
        const selectedFarm = farms.find((f: Farm) => f.id === farmId);
        state.farm = selectedFarm ?? farms[0];
        sessionStorage.setItem('farmId', state.farm?.id);

        state.farmUser = state.user?.farm_users.find(
          (f) => f.farm_id === state.farm?.id
        );
      } else {
        state.farmUser = state.user
          ? {
              ...state.user,
              user_id: state.user.id,
              farm_id: null,
              owner: null,
              main_user: null,
              selectedAbilities: null,
              active: null,
            }
          : null;
        state.farm = null;
      }
    },
    guard: {
      reducer: (state, action: PayloadAction<string>) => {
        state.guard = action.payload;
      },
      prepare: (guard: string = 'api') => ({ payload: guard }),
    },
    farm: (state, action) => {
      state.farm = action.payload;
      sessionStorage.setItem('farmId', state.farm?.id);
      state.farmUser = state.user?.farm_users.find(
        (f) => f.farm_id === state.farm?.id
      );
    },
    season: (state, action) => {
      state.season = action.payload;
    },
    addNotice: (state, action: PayloadAction<Notice>) => {
      state.notices.push(action.payload);
    },
    removeNotice: (state, action: PayloadAction<string>) => {
      state.notices = state.notices.filter(
        (notice) => notice.accessor !== action.payload
      );
    },
    toggleMaintenanceMode: (state, { payload = !state.maintenanceMode }) => {
      state.maintenanceMode = payload;
    },
    toggleSidebarMenuOpen: (
      state,
      { payload = !state.isSidebarMenuOpen }: PayloadAction<boolean>
    ) => {
      state.isSidebarMenuOpen = payload;
    },
    toggleSidebarMenuReduced: (
      state,
      { payload = !state.isSidebarMenuReduced }: PayloadAction<boolean>
    ) => {
      state.isSidebarMenuReduced = payload;
    },
    toggleProfileMenuOpen: (
      state,
      { payload = !state.isProfileMenuOpen }: PayloadAction<boolean>
    ) => {
      state.isProfileMenuOpen = payload;
    },
    systemConfiguration: (
      state,
      action: PayloadAction<SystemConfiguration[]>
    ) => {
      state.systemConfiguration =
        action.payload.reduce<SystemConfigurationValues>((values, config) => {
          values[config.key] = config.value;
          return values;
        }, {});
    },
    setMovementType: (state, { payload }) => {
      state.movementType = payload;
    },
    setFieldsFilter: (state, action: PayloadAction<string | null>) => {
      state.fieldsFilter = action.payload;
    },
    clearFieldsFilter: (state) => {
      state.fieldsFilter = null;
    },
    toggleMLTotalsChanged: (state, action: PayloadAction<boolean>) => {
      state.isMLTotalsChanged = action.payload;
    },
    toggleAnimalCalendarOpen: (
      state,
      { payload = !state.isAnimalCalendarOpen }: PayloadAction<boolean>
    ) => {
      state.isAnimalCalendarOpen = payload;
    },
    setSplitGeometries: (state, { payload }) => {
      state.split = payload;
    },
    setMergeGeometries: (state, { payload }) => {
      state.merge = payload;
    },
  },
});

export const {
  loading,
  loadingFragments,
  guard,
  user,
  addNotice,
  removeNotice,
  toggleMaintenanceMode,
  toggleSidebarMenuOpen,
  toggleSidebarMenuReduced,
  toggleProfileMenuOpen,
  farm,
  season,
  systemConfiguration,
  setMovementType,
  setFieldsFilter,
  clearFieldsFilter,
  toggleMLTotalsChanged,
  toggleAnimalCalendarOpen,
  setSplitGeometries,
  setMergeGeometries,
} = globalSlice.actions;
export default globalSlice;
